import React from 'react';
import { BaseLayout } from '@/components/layout';
import { useProjectTeam } from '../hooks/useProjectTeam';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Search, Trash2, UserPlus, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchableSelect } from '@/shared/ui/custom/searchable-select';

export const ProjectTeam: React.FC = () => {
  const {
    data,
    isLoading,
    error,
    success,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    formType,
    setFormType,
    addInternalUserForm,
    setAddInternalUserForm,
    inviteInternalUserForm,
    setInviteInternalUserForm,
    inviteExternalUserForm,
    setInviteExternalUserForm,
    handleAddInternalUser,
    handleInviteInternalUser,
    handleInviteExternalUser,
    handleDeleteUsers,
    selectedUsers,
    toggleUserSelection,
    toggleAllUsers,
    teamMemberRows,
    uniqueRoles,
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    availableUsers,
    loadingUsers,
  } = useProjectTeam();

  if (isLoading && !data) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 border-primary/30 bg-primary/10 text-foreground">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Table Container */}
          <div className="bg-card rounded-lg shadow border border-border">
            {/* Table Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Filter Button */}
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueRoles.map(role => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Search Bar */}
                <div className="flex items-center space-x-2 border border-border rounded-lg px-3 py-2 bg-input">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 bg-transparent"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {selectedUsers.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete ({selectedUsers.size})
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => setIsAddUserModalOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </div>
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedUsers.size === teamMemberRows.length && teamMemberRows.length > 0}
                      onCheckedChange={toggleAllUsers}
                    />
                  </TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone #</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMemberRows.length > 0 ? (
                  teamMemberRows.map(row => {
                    const key = `${row.type}-${row.id}`;
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.has(key)}
                            onCheckedChange={() => toggleUserSelection(key)}
                          />
                        </TableCell>
                        <TableCell>{row.role}</TableCell>
                        <TableCell>
                          {row.name}
                          {row.isPending && (
                            <span className="ml-2 text-xs text-muted-foreground">(Pending)</span>
                          )}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No project users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Add User Modal */}
          <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Add Member to {data?.project.name}</DialogTitle>
                <DialogDescription>
                  Add an existing user or invite a new member to the project
                </DialogDescription>
              </DialogHeader>

              {/* Tab Toggle */}
              <Tabs value={formType} onValueChange={(v) => setFormType(v as 'internal' | 'external')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="internal">Internal</TabsTrigger>
                  <TabsTrigger value="external">External</TabsTrigger>
                </TabsList>

                {/* Internal Forms Tab */}
                <TabsContent value="internal" className="space-y-6">
                  {/* Add Existing User Form */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Add Existing User</h3>
                    <form onSubmit={handleAddInternalUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="user">User *</Label>
                        {loadingUsers ? (
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading users...</span>
                          </div>
                        ) : (
                          <SearchableSelect
                            value={addInternalUserForm.user_id}
                            onValueChange={(value) =>
                              setAddInternalUserForm(prev => ({ ...prev, user_id: value }))
                            }
                            options={availableUsers.map(user => ({
                              value: String(user.id),
                              label: `${user.first_name} ${user.last_name} (${user.email})`
                            }))}
                            placeholder="Select a user"
                            searchPlaceholder="Search users..."
                            emptyMessage="No users found."
                          />
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="add-role">Role</Label>
                        <Input
                          id="add-role"
                          value={addInternalUserForm.role}
                          onChange={(e) =>
                            setAddInternalUserForm(prev => ({ ...prev, role: e.target.value }))
                          }
                          placeholder="Enter role"
                        />
                      </div>

                      <Button type="submit" className="w-full">
                        Add Member
                      </Button>
                    </form>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-popover px-2 text-muted-foreground">or</span>
                    </div>
                  </div>

                  {/* Invite New User Form */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Invite New User</h3>
                    <form onSubmit={handleInviteInternalUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="invite-email">Email Address *</Label>
                        <Input
                          id="invite-email"
                          type="email"
                          value={inviteInternalUserForm.email}
                          onChange={(e) =>
                            setInviteInternalUserForm(prev => ({ ...prev, email: e.target.value }))
                          }
                          placeholder="Enter email"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="invite-role">Role *</Label>
                        <Input
                          id="invite-role"
                          value={inviteInternalUserForm.role}
                          onChange={(e) =>
                            setInviteInternalUserForm(prev => ({ ...prev, role: e.target.value }))
                          }
                          placeholder="Enter role"
                        />
                      </div>

                      <Button type="submit" className="w-full">
                        Send Invitation
                      </Button>
                    </form>
                  </div>
                </TabsContent>

                {/* External Form Tab */}
                <TabsContent value="external">
                  <form onSubmit={handleInviteExternalUser} className="space-y-4">
                    <p className="text-sm text-muted-foreground">* = required field</p>

                    <div className="space-y-2">
                      <Label htmlFor="ext-role">Role *</Label>
                      <Input
                        id="ext-role"
                        value={inviteExternalUserForm.role}
                        onChange={(e) =>
                          setInviteExternalUserForm(prev => ({ ...prev, role: e.target.value }))
                        }
                        placeholder="Enter role"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ext-email">Email Address *</Label>
                      <Input
                        id="ext-email"
                        type="email"
                        value={inviteExternalUserForm.external_email}
                        onChange={(e) =>
                          setInviteExternalUserForm(prev => ({ ...prev, external_email: e.target.value }))
                        }
                        placeholder="Enter email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ext-company">Company Name *</Label>
                      <Input
                        id="ext-company"
                        value={inviteExternalUserForm.external_company_name}
                        onChange={(e) =>
                          setInviteExternalUserForm(prev => ({
                            ...prev,
                            external_company_name: e.target.value,
                          }))
                        }
                        placeholder="Enter company name"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label>Access Levels *</Label>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="asbuilds"
                          checked={inviteExternalUserForm.can_access_asbuilds}
                          onCheckedChange={(checked) =>
                            setInviteExternalUserForm(prev => ({
                              ...prev,
                              can_access_asbuilds: checked === true,
                            }))
                          }
                        />
                        <Label htmlFor="asbuilds" className="font-normal cursor-pointer">
                          Asbuilds
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="progress"
                          checked={inviteExternalUserForm.can_access_progress}
                          onCheckedChange={(checked) =>
                            setInviteExternalUserForm(prev => ({
                              ...prev,
                              can_access_progress: checked === true,
                            }))
                          }
                        />
                        <Label htmlFor="progress" className="font-normal cursor-pointer">
                          Progress
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="drones"
                          checked={inviteExternalUserForm.can_access_drones}
                          onCheckedChange={(checked) =>
                            setInviteExternalUserForm(prev => ({
                              ...prev,
                              can_access_drones: checked === true,
                            }))
                          }
                        />
                        <Label htmlFor="drones" className="font-normal cursor-pointer">
                          Drone Videos
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="drone-photos"
                          checked={inviteExternalUserForm.can_access_drone_photos}
                          onCheckedChange={(checked) =>
                            setInviteExternalUserForm(prev => ({
                              ...prev,
                              can_access_drone_photos: checked === true,
                            }))
                          }
                        />
                        <Label htmlFor="drone-photos" className="font-normal cursor-pointer">
                          Drone Photos
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="surveillance"
                          checked={inviteExternalUserForm.can_access_surveillance}
                          onCheckedChange={(checked) =>
                            setInviteExternalUserForm(prev => ({
                              ...prev,
                              can_access_surveillance: checked === true,
                            }))
                          }
                        />
                        <Label htmlFor="surveillance" className="font-normal cursor-pointer">
                          Surveillance
                        </Label>
                      </div>
                    </div>

                    <Button type="submit" className="w-full">
                      Add Member
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Modal */}
          <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Selected Users</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete {selectedUsers.size} selected user(s)?
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="space-x-2">
                <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeleteUsers}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </BaseLayout>
  );
};






